import React from 'react';
import { View, Text, StyleSheet, type ViewStyle } from 'react-native';
import type { ThemeColors } from '../../../../shared/theme';
import { useTranslation } from '../../../../shared/i18n';
import { appTheme } from '../../../../shared/theme';
import { useRtl } from '../../../../shared/i18n/useRtl';
import { ContentCard } from '../../../../components/common/ContentCard';
import { Clickable, PRESS_PRESETS } from '../../../../components/common/Clickable';
import { HorizontalLtrScrollView } from '../../../../components/ui/HorizontalLtrScrollView';
import { DASH_STUDIO_RADIUS, DASH_STUDIO_INNER, studioCardShell } from '../../../../shared/theme/studio/dashboardConstants';

const DEFAULT_NUM_COLUMNS = 9;
const DEFAULT_ITEM_GAP = appTheme.spacing.md;
const DEFAULT_MAX_HEIGHT = 280;

export interface DashboardScrollableGridCardSet3Props {
  colors: ThemeColors;
  /** Array of items to render in the grid. Each item can be any React node (e.g. a custom component with its own size and styling). */
  items: React.ReactNode[];
  /** When provided, the entire card is pressable and this is called on press (e.g. navigate to a screen). Omit for no container action. */
  on_container_press?: () => void;
  /** Number of columns in the grid. Default 2. */
  num_columns?: number;
  /** Gap between grid items. Default 12. */
  item_gap?: number;
  /** Max height of the scrollable area; content taller than this will scroll vertically. Default 280. */
  max_height?: number;
  /** Optional title shown above the grid. */
  title?: string;
  /** Optional key extractor for list items; used for React keys when items are not keyed. */
  key_extractor?: (index: number) => string;
  /** Optional style for the card container. */
  style?: ViewStyle;
  /** Optional style for the scroll content container. */
  content_container_style?: ViewStyle;
}

export function DashboardScrollableGridCardSet3({
  colors,
  items,
  on_container_press,
  num_columns: _num_columns = DEFAULT_NUM_COLUMNS,
  item_gap: _item_gap = DEFAULT_ITEM_GAP,
  max_height = DEFAULT_MAX_HEIGHT,
  title,
  key_extractor: _key_extractor,
  style,
  content_container_style,
}: DashboardScrollableGridCardSet3Props) {
  const { t } = useTranslation();
  const rtl = useRtl();
  const text_align: 'left' | 'right' = rtl.isRTL ? 'right' : 'left';
  const item_gap = _item_gap;

  const card_padding = appTheme.card.padding.lg;
  return (
    <ContentCard
      variant="outlined"
      borderless
      padding="lg"
      style={[styles.card, studioCardShell(colors), { backgroundColor: colors.surface }, style]}>
      {title != null && title !== '' ? (
        <View
          style={[
            styles.titleWrap,
            { paddingHorizontal: DASH_STUDIO_INNER, paddingTop: card_padding, paddingBottom: appTheme.spacing.md },
          ]}>
          {on_container_press != null ? (
            <Clickable
              onPress={on_container_press}
              accessibilityLabel={title}
              accessibilityRole="button"
              pressScale={PRESS_PRESETS.listRow.scale}
              haptics>
              <View style={styles.titleRow}>
                <Text
                  style={[
                    styles.title,
                    { color: colors.textLight, textAlign: text_align, writingDirection: rtl.isRTL ? 'rtl' : 'ltr' },
                  ]}
                  numberOfLines={1}>
                  {title}
                </Text>
                <Text style={[styles.titleLink, { color: colors.primary, writingDirection: rtl.isRTL ? 'rtl' : 'ltr' }]}>
                  {t('dashboard.viewAll')}
                </Text>
              </View>
            </Clickable>
          ) : (
            <Text
              style={[
                styles.title,
                { color: colors.textLight, textAlign: text_align, writingDirection: rtl.isRTL ? 'rtl' : 'ltr' },
              ]}
              numberOfLines={1}>
              {title}
            </Text>
          )}
        </View>
      ) : null}
      <HorizontalLtrScrollView
        style={[styles.scroll, { maxHeight: max_height || undefined }]}
        contentContainerStyle={[
          styles.scrollContent,
          {
            paddingVertical: card_padding + 4,
            paddingHorizontal: card_padding,
            paddingBottom: card_padding + 8,
            alignItems: 'flex-start',
            // Row on the scroll content, not a nested View with `minWidth: 0` (that collapsed width).
            flexDirection: 'row',
            flexWrap: 'nowrap',
            gap: item_gap,
          },
          content_container_style,
        ]}
        showsHorizontalScrollIndicator={false}
        nestedScrollEnabled
        horizontal>
        {items}
      </HorizontalLtrScrollView>
    </ContentCard>
  );
}

const styles = StyleSheet.create({
  card: {
    padding: 0,
    minWidth: 0,
    /** Nested menu / community tiles need visible soft shadows. */
    overflow: 'visible',
  },
  titleWrap: {},
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: appTheme.spacing.sm,
    minHeight: 24,
  },
  title: {
    ...appTheme.typography.captionSmall,
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 0.9,
    textTransform: 'uppercase' as const,
    flex: 1,
    minWidth: 0,
  },
  titleLink: {
    fontSize: 13,
    fontWeight: '800',
    flexShrink: 0,
  },
  scroll: {
    // flexGrow: 0,
    // flex: 1,
    margin: 0,
    padding: 0,
  },
  scrollContent: {},
  // cell: {
  //   minWidth: 0,
  // },
});
